import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { videos } from "@/mocks/videos";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function VideoDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const foundVideo = videos.find((v) => v.slug === slug);
  const video = useTranslatedList(foundVideo ? [foundVideo] : [], [
    "title",
    "description",
    "category",
  ])[0];
  const related = useTranslatedList(
    videos.filter((v) => v.slug !== slug && v.category === foundVideo?.category).slice(0, 3),
    ["title"],
  );

  useEffect(() => {
    if (video) {
      document.title = `${video.title} | L+TV Videos`;
    }
  }, [video]);

  if (!video) {
    return (
      <div className="pt-20 ltv-container py-16 text-center">
        <i className="ri-error-warning-line text-4xl text-foreground-300 mb-3"></i>
        <h1 className="text-xl font-bold text-foreground-800 mb-2">{t("video.notFound")}</h1>
        <Link to="/videos" className="ltv-btn-primary text-sm inline-flex items-center gap-2">
          <i className="ri-arrow-left-line"></i>
          {t("video.backToVideos")}
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <div className="flex items-center gap-2 mb-2 text-sm text-white/60">
            <Link to="/videos" className="hover:text-white transition-colors">{t("nav.videos")}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>{video.title}</span>
          </div>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Video Player Placeholder */}
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video mb-6">
              <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <button className="w-20 h-20 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors backdrop-blur-sm">
                  <i className="ri-play-fill text-white text-4xl ml-1"></i>
                </button>
              </div>
              {/* Controls */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
                <div className="flex items-center gap-3">
                  <button className="text-white/80 hover:text-white"><i className="ri-play-fill text-xl"></i></button>
                  <button className="text-white/80 hover:text-white"><i className="ri-volume-up-line text-lg"></i></button>
                  <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full w-1/3 bg-accent-500 rounded-full"></div>
                  </div>
                  <span className="text-white/60 text-xs">{video.duration}</span>
                  <button className="text-white/80 hover:text-white"><i className="ri-fullscreen-line text-lg"></i></button>
                </div>
              </div>
            </div>

            <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2">{video.title}</h1>
            <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4 flex-wrap">
              <span className="bg-primary-500 text-white text-xs font-semibold px-2 py-1 rounded">{video.category}</span>
              <span>{video.publishedAt}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{video.views} {t("video.views")}</span>
              <span>·</span>
              <span>{video.duration}</span>
            </div>
            <p className="text-foreground-700 leading-relaxed mb-6">{video.description}</p>

            <div className="flex flex-wrap gap-2 mb-6">
              {video.tags.map((tag) => (
                <span key={tag} className="bg-background-100 text-foreground-600 text-xs font-medium px-3 py-1.5 rounded-md">{tag}</span>
              ))}
            </div>

            {/* Share */}
            <div className="border-t border-background-200 pt-4">
              <p className="text-sm font-semibold text-foreground-700 mb-3">{t("video.share")}</p>
              <div className="flex gap-2">
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-md bg-[#1877F2] text-white text-sm"><i className="ri-facebook-fill"></i></a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-md bg-foreground-800 text-white text-sm"><i className="ri-twitter-x-fill"></i></a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-md bg-[#25D366] text-white text-sm"><i className="ri-whatsapp-fill"></i></a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-500 mb-4">{t("video.relatedVideos")}</h3>
            <div className="space-y-4">
              {related.map((v) => (
                <Link key={v.id} to={`/videos/${v.slug}`} className="group flex gap-3">
                  <div className="relative w-32 h-20 flex-shrink-0 rounded-md overflow-hidden">
                    <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 rounded">{v.duration}</div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-accent-600 transition-colors">{v.title}</p>
                    <p className="text-xs text-foreground-500 mt-1">{v.views} {t("video.views")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}